import React from 'react';
import { Target, Users, ShieldCheck, Zap } from 'lucide-react';
import SEO from '../components/SEO';

export default function AboutPage() {
    return (
        <div className="min-h-screen bg-white" data-testid="about-page">
            <SEO 
                title="About NSO - India | Trusted Online Store with Top Quality" 
                description="NSO India brings the top-quality premium products, affordable pricing, and a smooth online shopping store with a customer-first approach." 
            />
            {/* Hero Section */}
            <section className="bg-secondary py-20 md:py-32">
                <div className="max-w-7xl mx-auto px-4 md:px-8 text-center text-white">
                    <h1 className="font-heading text-4xl md:text-6xl mb-6">About Nso-India</h1>
                    <p className="font-body text-lg md:text-xl text-white/70 max-w-3xl mx-auto leading-relaxed">
                        We are NSO SERVICES PRIVATE LIMITED. Our mission is to bridge the gap between premium products and accessibility, offering an unparalleled online shopping experience across India.
                    </p>
                </div>
            </section>

            {/* Content Section */}
            <section className="py-16 md:py-24">
                <div className="max-w-7xl mx-auto px-4 md:px-8">
                    <div className="grid md:grid-cols-2 gap-16 items-center">
                        <div>
                            <h2 className="font-heading text-3xl md:text-4xl text-secondary mb-6">Our Journey</h2>
                            <div className="space-y-4 font-body text-muted-foreground leading-relaxed">
                                <p>
                                    Founded with a clear vision to simplify and elevate digital commerce, Nso-India has rapidly grown into a trusted destination for quality-conscious consumers. We recognized early on that shopping online shouldn't just be about convenience—it should be about trust, authenticity, and premium service.
                                </p>
                                <p>
                                    Today, we specialize in offering an expertly curated selection of high-end optical eyewear and cutting-edge mobile smartphones. Whether you're upgrading your tech or finding the perfect frame, Nso-India is built to serve your needs with precision.
                                </p>
                            </div>
                        </div>
                        <div className="bg-surface rounded-3xl p-8 relative overflow-hidden">
                            <div className="absolute inset-0 bg-primary/5 pattern-dots pattern-primary/10 pattern-bg-transparent pattern-size-4 pattern-opacity-100 mix-blend-multiply" />
                            <div className="relative z-10 grid grid-cols-2 gap-6">
                                <div className="bg-white p-6 rounded-2xl shadow-soft">
                                    <h4 className="font-heading text-3xl text-primary mb-2">10k+</h4>
                                    <p className="font-body text-sm text-secondary font-medium">Happy Customers</p>
                                </div>
                                <div className="bg-white p-6 rounded-2xl shadow-soft">
                                    <h4 className="font-heading text-3xl text-primary mb-2">100%</h4>
                                    <p className="font-body text-sm text-secondary font-medium">Genuine Products</p>
                                </div>
                                <div className="bg-white p-6 rounded-2xl shadow-soft">
                                    <h4 className="font-heading text-3xl text-primary mb-2">24/7</h4>
                                    <p className="font-body text-sm text-secondary font-medium">Customer Support</p>
                                </div>
                                <div className="bg-white p-6 rounded-2xl shadow-soft">
                                    <h4 className="font-heading text-3xl text-primary mb-2">Fast</h4>
                                    <p className="font-body text-sm text-secondary font-medium">Nationwide Delivery</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Core Values */}
            <section className="bg-surface py-16 md:py-24">
                <div className="max-w-7xl mx-auto px-4 md:px-8">
                    <div className="text-center mb-16">
                        <h2 className="font-heading text-3xl md:text-4xl text-secondary mb-4">Our Core Values</h2>
                        <p className="font-body text-muted-foreground text-lg">The principles that guide everything we do.</p>
                    </div>

                    <div className="grid md:grid-cols-4 gap-8">
                        {[
                            { icon: ShieldCheck, title: "Authenticity", desc: "We guarantee 100% genuine products sourced directly from authorized brands." },
                            { icon: Target, title: "Quality", desc: "Every item in our catalog passes rigorous quality checks before reaching you." },
                            { icon: Users, title: "Customer First", desc: "Your satisfaction is our ultimate goal. We are here to support you at every step." },
                            { icon: Zap, title: "Innovation", desc: "We continually evolve our platform to provide a seamless and modern shopping journey." }
                        ].map((value, idx) => (
                            <div key={idx} className="bg-white rounded-3xl p-8 shadow-sm hover:shadow-soft transition-all text-center group">
                                <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform">
                                    <value.icon className="w-8 h-8 text-primary" />
                                </div>
                                <h3 className="font-heading text-xl text-secondary mb-3">{value.title}</h3>
                                <p className="font-body text-muted-foreground text-sm leading-relaxed">{value.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
        </div>
    );
}
